/**
 * Avatarlar (demo «Lexo v3» `avSVG`/`myAvatar`/`frameCSS`): yasalgan avatar (SVG), yuklangan surat yoki bosh harf.
 * Boshqa o'yinchilar (reyting, so'z jangi) — server `ApiPlayer`: surat `/v1/avatars/{id}` dan.
 */
import type { CSSProperties } from 'react';
import { useStore, type AvCfg } from '../store';
import { API_URL } from '../lib/api';
import type { ApiPlayer } from '../lib/sync';
import { Ic } from './Icon';

export const AVOPT = {
  bg: ['#1A66F5', '#F97316', '#2e7a55', '#3f78a3', '#c2410c', '#be185d', '#0e7490', '#0B1B3F'],
  skin: ['#f8d5b8', '#eebd93', '#d9a066', '#b07a4f', '#7d5236'],
  hair: ['Yoʻq', 'Qisqa', 'Yon tarash', 'Uzun', 'Tugun', 'Jingalak', 'Roʻmol'],
  hairC: ['#0B1B3F', '#5a3825', '#a0662f', '#d9b26a', '#9a9a9a', '#1A66F5'],
  eyes: ['Nuqta', 'Kulgan', 'Koʻz qisgan'],
  mouth: ['Tabassum', 'Kulgi', 'Jiddiy'],
  acc: [['Yoʻq', 0], ['Koʻzoynak', 0], ['Kepka', 0], ['Quloqchin', 1000], ['Toj', 5000]] as Array<[string, number]>,
};
export const AV_DEFAULT: AvCfg = { bg: 0, skin: 1, hair: 1, hairC: 0, eyes: 0, mouth: 0, acc: 0 };

/** [nom, rang, kerakli XP, shart] — "Olov" 7 kunlik streak bilan ochiladi. */
export const FRAMES: Array<[string, string | null, number, ('streak7' | undefined)?]> = [
  ['Yoʻq', null, 0], ['Binafsha', '#1A66F5', 0], ['Shaftoli', '#FFC629', 0], ['Yashil', '#2e7a55', 2000], ['Oltin', '#e2a33d', 7000],
  ['Olov', 'linear-gradient(135deg,#0B3FB0,#4A86FF)', 0, 'streak7'],
];

export const AVC = ['#F97316', '#3f78a3', '#2e7a55', '#7A5200', '#0B3FB0', '#c2410c', '#0e7490', '#7c3aed', '#b45309', '#be185d'];
export const colorOf = (s: string) => AVC[[...s].reduce((a, ch) => a + ch.charCodeAt(0), 0) % AVC.length]!;

/** Yasalgan avatar SVG matni (100×100). */
export function avSvg(c: Partial<AvCfg>, sz: number): string {
  const sk = AVOPT.skin[c.skin ?? 0], hc = AVOPT.hairC[c.hairC ?? 0], bg = AVOPT.bg[c.bg ?? 0];
  const hair = ['',
    `<path d="M28 44c0-16 10-26 22-26s22 10 22 26c-6-8-14-11-22-11s-16 3-22 11z" fill="${hc}"/>`,
    `<path d="M27 46c0-18 11-28 23-28 14 0 24 10 23 28-9-12-24-14-38-6-3 2-6 4-8 6z" fill="${hc}"/>`,
    `<path d="M26 48c0-19 11-30 24-30s24 11 24 30v26c-4 0-6-4-6-10V47c-8-6-28-6-36 0v17c0 6-2 10-6 10z" fill="${hc}"/>`,
    `<circle cx="50" cy="16" r="9" fill="${hc}"/><path d="M28 44c0-16 10-25 22-25s22 9 22 25c-6-7-14-10-22-10s-16 3-22 10z" fill="${hc}"/>`,
    `<g fill="${hc}">${[[32, 30], [40, 23], [50, 20], [60, 23], [68, 30], [72, 40], [28, 40]].map(([x, y]) => `<circle cx="${x}" cy="${y}" r="9"/>`).join('')}</g>`,
    `<path d="M22 50c0-20 12-34 28-34s28 14 28 34v40H22z" fill="${hc}"/><path d="M33 52c0-11 7-19 17-19s17 8 17 19" fill="none" stroke="rgba(255,255,255,.18)" stroke-width="3"/>`,
  ][c.hair ?? 0] ?? '';
  const scarf = (c.hair ?? 0) === 6;
  const eyes = [
    '<circle cx="42" cy="52" r="3.4" fill="#0B1B3F"/><circle cx="58" cy="52" r="3.4" fill="#0B1B3F"/>',
    '<path d="M38 53q4-5 8 0M54 53q4-5 8 0" fill="none" stroke="#0B1B3F" stroke-width="2.6" stroke-linecap="round"/>',
    '<circle cx="42" cy="52" r="3.4" fill="#0B1B3F"/><path d="M54 53q4-4 8 0" fill="none" stroke="#0B1B3F" stroke-width="2.6" stroke-linecap="round"/>',
  ][c.eyes ?? 0] ?? '';
  const mouth = [
    '<path d="M43 63q7 6 14 0" fill="none" stroke="#0B1B3F" stroke-width="2.6" stroke-linecap="round"/>',
    '<path d="M42 62q8 10 16 0z" fill="#0B1B3F"/><path d="M45 64q5 3 10 0" fill="#F97316"/>',
    '<path d="M44 64h12" stroke="#0B1B3F" stroke-width="2.6" stroke-linecap="round"/>',
  ][c.mouth ?? 0] ?? '';
  const acc = ['',
    '<g fill="none" stroke="#0B1B3F" stroke-width="2.2"><circle cx="42" cy="52" r="7"/><circle cx="58" cy="52" r="7"/><path d="M49 52h2"/></g>',
    '<path d="M26 40c2-14 12-21 24-21s22 7 24 21z" fill="#F97316"/><path d="M50 40h28c2 0 3 3 0 4H50z" fill="#c2410c"/>',
    '<path d="M27 52c0-16 10-27 23-27s23 11 23 27" fill="none" stroke="#0B1B3F" stroke-width="4"/><rect x="21" y="48" width="9" height="15" rx="4" fill="#0B1B3F"/><rect x="70" y="48" width="9" height="15" rx="4" fill="#0B1B3F"/>',
    '<path d="M33 30l5-12 6 8 6-12 6 12 6-8 5 12z" fill="#f2b632" stroke="#c98a12" stroke-width="1.5"/>',
  ][c.acc ?? 0] ?? '';
  return `<svg viewBox="0 0 100 100" width="${sz}" height="${sz}" aria-hidden="true"><circle cx="50" cy="50" r="50" fill="${bg}"/>${scarf ? hair : ''}<path d="M24 100c2-16 13-24 26-24s24 8 26 24z" fill="${AVOPT.bg[(c.bg ?? 0) + 1] ?? '#0B3FB0'}" opacity=".9"/><rect x="44" y="66" width="12" height="12" fill="${sk}"/><ellipse cx="50" cy="52" rx="21" ry="23" fill="${sk}"/>${scarf ? '' : hair}${eyes}${mouth}${acc}</svg>`;
}

/** Ramka: rang — halqa (box-shadow), gradient — fon + padding. */
function frameOf(name: string | null | undefined): { ring?: string; grad?: string } {
  const f = FRAMES.find((x) => x[0] === name);
  if (!f || !f[1]) return {};
  return f[1].startsWith('linear') ? { grad: f[1] } : { ring: f[1] };
}

interface AvProps { size: number; className?: string; style?: CSSProperties }

function Shell({ size, className, style, frame, bg, children }: AvProps & { frame?: string | null; bg?: string; children: React.ReactNode }) {
  const fr = frameOf(frame);
  const st: CSSProperties = { width: size, height: size, background: fr.grad ?? bg ?? 'transparent', ...(fr.grad ? { padding: 3 } : {}), ...(fr.ring ? { boxShadow: `0 0 0 3px ${fr.ring}` } : {}), fontSize: Math.round(size * 0.4), ...style };
  return <span className={`av2 myav ${className ?? ''}`} style={st}>{children}</span>;
}

/** Joriy foydalanuvchi avatari (store'dan). */
export function MyAvatar({ size, className, style }: AvProps) {
  const avatar = useStore((s) => s.avatar), frame = useStore((s) => s.frame), name = useStore((s) => s.name);
  const inner = frameOf(frame).grad ? size - 6 : size;
  if (avatar?.t === 'photo') return <Shell size={size} className={className} style={style} frame={frame}><img src={avatar.d} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }} /></Shell>;
  if (avatar?.t === 'build') return <Shell size={size} className={className} style={style} frame={frame}><span style={{ display: 'contents' }} dangerouslySetInnerHTML={{ __html: avSvg(avatar.c, inner) }} /></Shell>;
  return <Shell size={size} className={className} style={style} frame={frame} bg="#1A66F5"><span>{(name || 'L')[0]!.toUpperCase()}</span></Shell>;
}

/** Boshqa o'yinchi avatari (server profili). */
export function PlayerAvatar({ p, size, className, style }: AvProps & { p: Pick<ApiPlayer, 'id' | 'n' | 'avatar' | 'photo' | 'frame'> & { nick?: string | null } }) {
  const inner = frameOf(p.frame).grad ? size - 6 : size;
  if (p.photo) return <Shell size={size} className={className} style={style} frame={p.frame}><img src={`${API_URL}/v1/avatars/${p.id}`} alt="" loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }} /></Shell>;
  if (p.avatar?.t === 'build') return <Shell size={size} className={className} style={style} frame={p.frame}><span style={{ display: 'contents' }} dangerouslySetInnerHTML={{ __html: avSvg(p.avatar.c, inner) }} /></Shell>;
  const label = p.nick || p.n || '?';
  return <Shell size={size} className={className} style={style} frame={p.frame} bg={colorOf(label)}><span>{label[0]!.toUpperCase()}</span></Shell>;
}

/** Lexo bot avatari. */
export function BotAvatar({ size }: { size: number }) {
  return <span className="av2" style={{ background: 'linear-gradient(135deg,#1A66F5,#5B93FF)', width: size, height: size }}><Ic n="bot" size={Math.round(size * 0.5)} style={{ color: '#fff' }} /></span>;
}

/** Oddiy harfli avatar (raqib noma'lum / simulyatsiya). */
export function LetterAvatar({ n, c, size }: { n: string; c?: string; size: number }) {
  return <span className="av2" style={{ background: c ?? colorOf(n), width: size, height: size, fontSize: Math.round(size * 0.4) }}>{(n || '?')[0]!.toUpperCase()}</span>;
}
